import React, { useState, useEffect, useMemo, useCallback } from 'react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import axios from 'axios';
import MainLayout from '../../../layout/main-layout';
import { APP_API_URL, APP_NAME } from '../../../utils';
import formatNumber, { defaultFilterDates, formatDate } from '../../../utils/functions';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';

const PER_PAGE = 10;

const SortIcon = ({ active, direction }) => (
  <span className="ms-1 opacity-75">{active ? (direction === 'asc' ? '↑' : '↓') : '↕'}</span>
);

const getSortValue = (item, key) => {
  if (key === 'variant') return item.variant?.name ?? '';
  if (key === 'user') return item.user?.name ?? '';
  if (key === 'quantity') return parseFloat(item.quantity) || 0;
  if (key === 'prix_unitaire_revient') return parseFloat(item.prix_unitaire_revient) || 0;
  if (key === 'total_cost_price') return parseFloat(item.total_cost_price) || 0;
  if (key === 'created_at') return item.created_at ?? '';
  if (key === 'note') return item.note ?? '';
  return item[key] ?? '';
};

export default function BrokenTilesList() {
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;

  const [allData, setAllData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [appliedFilters, setAppliedFilters] = useState(() => ({ search: '', ...defaultFilterDates() }));
  const [draftFilters, setDraftFilters] = useState(() => defaultFilterDates());
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/broken-tiles/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          fromDate: appliedFilters.dateFrom || undefined,
          toDate: appliedFilters.dateTo || undefined,
          search: appliedFilters.search || undefined,
        },
      });
      setAllData(response.data?.broken_tiles || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des carreaux cassés :', error);
      setAllData([]);
    } finally {
      setIsLoading(false);
    }
  }, [appliedFilters.dateFrom, appliedFilters.dateTo, appliedFilters.search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedFilters((prev) => (
        prev.search === searchInput ? prev : { ...prev, search: searchInput }
      ));
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const filteredItems = allData;

  const sortedItems = useMemo(() => {
    const sorted = [...filteredItems];
    const { key, direction } = sortConfig;
    const dir = direction === 'asc' ? 1 : -1;
    sorted.sort((a, b) => {
      const aValue = getSortValue(a, key);
      const bValue = getSortValue(b, key);
      if (key === 'quantity' || key === 'prix_unitaire_revient' || key === 'total_cost_price') {
        return (aValue - bValue) * dir;
      }
      if (key === 'created_at') {
        return (new Date(aValue) - new Date(bValue)) * dir;
      }
      const aStr = String(aValue);
      const bStr = String(bValue);
      if (aStr < bStr) return -1 * dir;
      if (aStr > bStr) return 1 * dir;
      return 0;
    });
    return sorted;
  }, [filteredItems, sortConfig]);

  const stats = useMemo(
    () => ({
      total: filteredItems.length,
      m2Total: filteredItems.reduce((acc, item) => acc + (parseFloat(item.quantity) || 0), 0),
      costTotal: filteredItems.reduce(
        (acc, item) => acc + (parseFloat(item.total_cost_price) || 0),
        0
      ),
    }),
    [filteredItems]
  );

  const lineCost = (item) => parseFloat(item.prix_unitaire_revient) || 0;
  const lineTotalCost = (item) =>
    parseFloat(item.total_cost_price) || (parseFloat(item.quantity) || 0) * lineCost(item);

  const totalPages = Math.max(1, Math.ceil(sortedItems.length / PER_PAGE));
  const indexOfLast = currentPage * PER_PAGE;
  const indexOfFirst = indexOfLast - PER_PAGE;
  const currentItems = sortedItems.slice(indexOfFirst, indexOfLast);

  const colSpan = isRole13 ? 7 : 8;

  const handlePageChange = (direction) => {
    if (direction === 'next' && currentPage < totalPages) setCurrentPage((p) => p + 1);
    else if (direction === 'prev' && currentPage > 1) setCurrentPage((p) => p - 1);
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const isSortActive = (key) => sortConfig.key === key;

  const handleApplyFilters = (e) => {
    e.preventDefault();
    setAppliedFilters((prev) => ({
      ...prev,
      dateFrom: draftFilters.dateFrom,
      dateTo: draftFilters.dateTo,
    }));
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setSearchInput('');
    setDraftFilters(defaultFilterDates());
    setAppliedFilters({ search: '', ...defaultFilterDates() });
    setCurrentPage(1);
    setSortConfig({ key: 'created_at', direction: 'desc' });
  };

  const handleRefresh = async () => {
    await fetchData();
    setCurrentPage(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Êtes-vous sûr ?',
      text: 'Cette action est irréversible.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#ff4b4b',
    });

    if (!result.isConfirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${APP_API_URL}/broken-tiles/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await Swal.fire({
        icon: 'success',
        title: 'Supprimé',
        text: 'Le carreau cassé a été supprimé.',
        timer: 2000,
        showConfirmButton: false,
      });
      fetchData();
    } catch (error) {
      console.error('Erreur lors de la suppression :', error);
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: 'Une erreur est survenue lors de la suppression.',
      });
    }
  };

  const exportRows = (items) =>
    items.map((item) => ({
      Produit: item.variant?.name ?? '—',
      'Quantité (m²)': parseFloat(item.quantity || 0),
      'Coût revient / m² (XOF)': lineCost(item),
      'Perte potentielle (XOF)': lineTotalCost(item),
      Date: formatDate(item.created_at),
      Note: item.note || '—',
      'Ajouté par': item.user?.name ?? '—',
    }));

  const exportToPDF = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const doc = new jsPDF();
    doc.text('Carreaux cassés', 14, 16);
    doc.autoTable({
      head: [['Produit', 'Quantité (m²)', 'Coût revient / m²', 'Perte potentielle', 'Date', 'Note', 'Ajouté par']],
      body: rows.map((r) => [
        r.Produit,
        r['Quantité (m²)'],
        formatNumber(r['Coût revient / m² (XOF)']),
        formatNumber(r['Perte potentielle (XOF)']),
        r.Date,
        r.Note,
        r['Ajouté par'],
      ]),
      startY: 24,
      headStyles: { fillColor: [88, 204, 2] },
    });
    doc.save(`Carreaux Cassés ${APP_NAME}.pdf`);
  };

  const exportToExcel = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Carreaux Cassés');
    XLSX.writeFile(wb, `Carreaux Cassés ${APP_NAME}.xlsx`);
  };

  const handlePrint = () => {
    const rows = exportRows(filteredItems.length ? filteredItems : currentItems);
    const printWindow = window.open('', '', 'height=800,width=1200');
    const tableRows = rows
      .map(
        (r) => `
      <tr>
        <td>${r.Produit}</td>
        <td>${r['Quantité (m²)']}</td>
        <td>${formatNumber(r['Coût revient / m² (XOF)'])} XOF</td>
        <td>${formatNumber(r['Perte potentielle (XOF)'])} XOF</td>
        <td>${r.Date}</td>
        <td>${r.Note}</td>
        <td>${r['Ajouté par']}</td>
      </tr>`
      )
      .join('');

    printWindow.document.write(`
      <html><head><title>Carreaux cassés</title>
      <style>body{font-family:Arial,sans-serif}table{width:100%;border-collapse:collapse}
      table,th,td{border:1px solid #ccc}th,td{padding:8px}</style></head>
      <body><h1>Liste des carreaux cassés</h1><table>
      <thead><tr><th>Produit</th><th>Quantité (m²)</th><th>Coût revient / m²</th><th>Perte potentielle</th><th>Date</th><th>Note</th><th>Ajouté par</th></tr></thead>
      <tbody>${tableRows}</tbody></table></body></html>`);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Administration</p>
          <h1 className="duo-hero-title">Carreaux cassés</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Suivi des carreaux cassés : chaque enregistrement représente une perte potentielle (coût de revient).
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Enregistrements (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.total}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Total m² (filtre)</p>
            <p className="duo-kpi-chip__value">{stats.m2Total.toFixed(2)}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Perte potentielle (filtre)</p>
            <p className="duo-kpi-chip__value" style={{ fontSize: '1rem', color: '#c0392b' }}>
              {formatNumber(stats.costTotal)} XOF
            </p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <span className="fw-bold" style={{ color: '#3c3c3c', fontSize: '0.9rem' }}>
              Liste des carreaux cassés
            </span>
            <div className="duo-toolbar__actions">
              <button type="button" title="PDF" onClick={exportToPDF} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-pdf" />
              </button>
              <button type="button" title="Excel" onClick={exportToExcel} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-filetype-xls" />
              </button>
              <button type="button" title="Imprimer" onClick={handlePrint} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-printer" />
              </button>
              <button type="button" title="Rafraîchir" onClick={handleRefresh} className="duo-btn duo-btn--ghost duo-btn--icon">
                <i className="bi bi-arrow-clockwise" />
              </button>
              <button
                type="button"
                title="Filtres"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={`duo-btn duo-btn--ghost duo-btn--icon duo-btn--filter${filtersOpen ? ' is-open' : ''}`}
              >
                <i className={`bi ${filtersOpen ? 'bi-x-lg' : 'bi-funnel-fill'}`} />
              </button>
              {!isRole13 && (
                <Link to="/admin/broken-tiles/create" className="duo-btn duo-btn--primary">
                  <i className="bi bi-plus" /> Nouveau
                </Link>
              )}
            </div>
          </div>

          <div className="px-3 pt-2 pb-2 border-bottom" style={{ borderColor: '#f0f0f0' }}>
            <input
              type="search"
              className="duo-input"
              placeholder="Rechercher produit, note ou utilisateur..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>

          {filtersOpen && (
            <div className="duo-filters">
              <form onSubmit={handleApplyFilters}>
                <div className="duo-filters__grid">
                  <div>
                    <label className="duo-form-label">Date de début</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.dateFrom}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateFrom: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="duo-form-label">Date de fin</label>
                    <input
                      type="date"
                      className="duo-input"
                      value={draftFilters.dateTo}
                      onChange={(e) => setDraftFilters((prev) => ({ ...prev, dateTo: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="d-flex justify-content-end gap-2">
                  <button type="button" className="duo-btn duo-btn--ghost" onClick={handleResetFilters}>
                    Réinitialiser
                  </button>
                  <button type="submit" className="duo-btn duo-btn--primary">
                    <i className="bi bi-search" /> Appliquer les filtres
                  </button>
                </div>
              </form>
            </div>
          )}

          {(appliedFilters.dateFrom || appliedFilters.dateTo) && (
            <div className="px-3 py-2 border-bottom small text-muted" style={{ borderColor: '#f0f0f0' }}>
              Période : {appliedFilters.dateFrom || '…'} → {appliedFilters.dateTo || '…'}
            </div>
          )}

          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th onClick={() => requestSort('variant')}>
                    Produit <SortIcon active={isSortActive('variant')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('quantity')}>
                    Quantité (m²) <SortIcon active={isSortActive('quantity')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('prix_unitaire_revient')}>
                    Coût revient / m²{' '}
                    <SortIcon active={isSortActive('prix_unitaire_revient')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('total_cost_price')}>
                    Perte potentielle{' '}
                    <SortIcon active={isSortActive('total_cost_price')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('created_at')}>
                    Date <SortIcon active={isSortActive('created_at')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('note')}>
                    Note <SortIcon active={isSortActive('note')} direction={sortConfig.direction} />
                  </th>
                  <th onClick={() => requestSort('user')}>
                    Ajouté par <SortIcon active={isSortActive('user')} direction={sortConfig.direction} />
                  </th>
                  {!isRole13 && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={colSpan} className="duo-empty-row">
                      Chargement des carreaux cassés…
                    </td>
                  </tr>
                ) : currentItems.length > 0 ? (
                  currentItems.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-bold">{item.variant?.name ?? '—'}</td>
                      <td>{parseFloat(item.quantity || 0).toFixed(2)}</td>
                      <td>{formatNumber(lineCost(item))} XOF</td>
                      <td className="fw-semibold" style={{ color: '#c0392b' }}>
                        {formatNumber(lineTotalCost(item))} XOF
                      </td>
                      <td className="text-nowrap">{formatDate(item.created_at)}</td>
                      <td>{item.note || '—'}</td>
                      <td>{item.user?.name ?? '—'}</td>
                      {!isRole13 && (
                        <td>
                          <div className="duo-table__actions">
                            <button
                              type="button"
                              title="Supprimer"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              onClick={() => handleDelete(item.id)}
                            >
                              <i className="bi bi-trash" style={{ color: '#ff4b4b' }} />
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={colSpan} className="duo-empty-row">
                      Aucun carreau cassé correspondant aux critères.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {currentItems.length > 0 && (
            <div className="duo-footer-bar">
              <span>
                {indexOfFirst + 1} – {Math.min(indexOfLast, sortedItems.length)} sur {sortedItems.length}{' '}
                entrée{sortedItems.length > 1 ? 's' : ''}
              </span>
              <ul className="duo-pagination">
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--ghost"
                    disabled={isLoading || currentPage === 1}
                    onClick={() => handlePageChange('prev')}
                  >
                    Précédent
                  </button>
                </li>
                <li>
                  <span className="duo-pagination__info px-2">
                    Page {currentPage} / {totalPages}
                  </span>
                </li>
                <li>
                  <button
                    type="button"
                    className="duo-btn duo-btn--primary"
                    disabled={isLoading || currentPage >= totalPages}
                    onClick={() => handlePageChange('next')}
                  >
                    Suivant
                  </button>
                </li>
              </ul>
            </div>
          )}
        </section>
      </div>
    </MainLayout>
  );
}
